/**
 * Workflows list page — DataTable of every automation flow in this org. Row
 * actions: Edit (open the editor), Duplicate, Delete. "New Workflow" creates an
 * empty graph (saved immediately) and opens the editor on it.
 *
 * Data + persistence come from useWorkflows(); create/duplicate/delete save
 * synchronously so navigating straight into the editor can't race its refetch.
 */
import { useState } from 'react';
import { Plus, Copy, Trash2, Pencil, AlertCircle, Workflow as WorkflowIcon } from 'lucide-react';
import { useWorkflows } from './useWorkflows';
import { iconForKey } from './nodeTypes';
import type { WorkflowGraph } from './types';
import { DataTable, type Column, type RowAction } from '../DataTable';
import { Skeleton, SkeletonTable } from '../Skeleton';

interface WorkflowListProps {
  onOpen: (id: string) => void;
}

export function WorkflowList({ onOpen }: WorkflowListProps) {
  const { graphs, loading, error, createGraph, duplicateGraph, deleteGraph } = useWorkflows();
  const [busy, setBusy] = useState(false);

  const handleNew = async () => {
    setBusy(true);
    try {
      const id = await createGraph();
      if (id) onOpen(id);
    } finally { setBusy(false); }
  };

  const handleDuplicate = async (id: string) => {
    await duplicateGraph(id);
  };

  const handleDelete = async (g: WorkflowGraph) => {
    if (!window.confirm(`Delete "${g.name}"? This cannot be undone.`)) return;
    await deleteGraph(g.id);
  };

  const columns: Column<WorkflowGraph>[] = [
    {
      key: 'name',
      header: 'Workflow',
      render: (g) => {
        const Icon = iconForKey(g.iconKey);
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="p-1.5 rounded-md shrink-0" style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold truncate" style={{ color: 'var(--ink)' }}>{g.name}</div>
              {g.description ? (
                <div className="text-xs truncate" style={{ color: 'var(--ink-secondary)', maxWidth: 420 }}>{g.description}</div>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      key: 'category',
      header: 'Category',
      render: (g) => (
        <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ color: 'var(--ink-secondary)', background: 'var(--ink-faint)' }}>
          {g.category}
        </span>
      ),
    },
    {
      key: 'nodes',
      header: 'Nodes',
      align: 'right',
      render: (g) => <span className="font-mono" style={{ color: 'var(--ink)' }}>{g.nodes.length}</span>,
    },
    {
      key: 'edges',
      header: 'Connections',
      align: 'right',
      render: (g) => <span className="font-mono" style={{ color: 'var(--ink)' }}>{g.edges.length}</span>,
    },
  ];

  const actions: RowAction<WorkflowGraph>[] = [
    { key: 'edit', label: 'Edit', icon: Pencil, onClick: (g) => onOpen(g.id), title: 'Edit workflow' },
    { key: 'dup', label: 'Duplicate', icon: Copy, onClick: (g) => handleDuplicate(g.id), title: 'Duplicate workflow' },
    { key: 'del', label: 'Delete', icon: Trash2, onClick: (g) => handleDelete(g), tone: 'danger', title: 'Delete workflow' },
  ];

  if (loading) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading workflows">
        <Skeleton width={160} height={11} rounded={3} />
        <SkeletonTable rows={5} cols={['40%', '16%', '12%', '14%']} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2" style={{ color: 'var(--danger, #D32F2F)' }}>
        <AlertCircle size={16} /> {error}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <DataTable
        rows={graphs}
        columns={columns}
        rowKey={(g) => g.id}
        actions={actions}
        onRowClick={(g) => onOpen(g.id)}
        searchKeys={[(g) => g.name, (g) => g.description, (g) => g.category]}
        searchPlaceholder="Search workflows…"
        pageSize={0}
        emptyState={(
          <div className="flex flex-col items-center gap-2">
            <WorkflowIcon className="w-6 h-6 opacity-40" />
            <div className="font-semibold" style={{ color: 'var(--ink)' }}>No workflows yet</div>
            <div className="text-xs" style={{ color: 'var(--ink-secondary)' }}>Create one to get started.</div>
          </div>
        )}
        toolbar={(
          <button
            onClick={handleNew}
            disabled={busy}
            className="flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-1.5 rounded transition disabled:opacity-50"
            style={{ background: 'var(--accent)' }}
          >
            <Plus className="w-3.5 h-3.5" />
            New Workflow
          </button>
        )}
      />
    </div>
  );
}
